import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, IconButton } from "@material-tailwind/react";
import { MagnifyingGlassIcon, XCircleIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import {
    getAppointmentCustomers,
    getAppointmentSubjectsAdmin,
    getAvailableSlots,
    createAppointmentAdmin,
} from "../../store/slices/appointmentSlice";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const FormField = ({ label, children, error, required = false }) => (
    <div className="w-full">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {error && <p className="text-red-600 text-xs mt-1.5 font-medium">{error}</p>}
    </div>
);

export default function CreateAppointment() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        customers,
        customersLoading,
        customersError,
        subjectsAdmin,
        availableSlots,
        loading,
    } = useSelector((state) => state.appointment);

    const todayKey = useMemo(() => toDateKey(new Date()), []);

    /* ---------------- Customer search ---------------- */
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerPage, setCustomerPage] = useState(1);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const customerBoxRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const dropdownRef = useRef(null);
    const { t } = useTranslation('common');

    useEffect(() => {
        dispatch(getAppointmentSubjectsAdmin());
    }, [dispatch]);

    const fetchCustomers = useCallback((page, search) => {
        return dispatch(
            getAppointmentCustomers({
                page,
                search,
                per_page: 5,
            })
        );
    }, [dispatch]);

    useEffect(() => {
        if (!showCustomerDropdown) return;

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

        searchDebounceRef.current = setTimeout(() => {
            setCustomerPage(1);
            fetchCustomers(1, customerSearch);
        }, 350);

        return () => clearTimeout(searchDebounceRef.current);
    }, [customerSearch, showCustomerDropdown, fetchCustomers]);

    const handleCustomerFocus = () => {
        setShowCustomerDropdown(true);
    };

    // close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (customerBoxRef.current && !customerBoxRef.current.contains(e.target)) {
                setShowCustomerDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerDropdown(false);
        setCustomerSearch("");
        setErrors((prev) => ({ ...prev, customer: undefined }));
    };

    const handleClearCustomer = () => {
        setSelectedCustomer(null);
    };

    /* ---------------- Date / Slots ---------------- */
    const [selectedDate, setSelectedDate] = useState(todayKey);
    const [selectedTime, setSelectedTime] = useState("");
    const [slotsLoading, setSlotsLoading] = useState(false);

    useEffect(() => {
        const loadSlots = async () => {
            setSlotsLoading(true);
            setSelectedTime("");
            await dispatch(getAvailableSlots({ appointment_date: selectedDate }));
            setSlotsLoading(false);
        };
        if (selectedDate) loadSlots();
    }, [selectedDate, dispatch]);

    const currentDate = new Date(selectedDate);

    const monthDays = useMemo(() => {
        const current = new Date(selectedDate);
        const year = current.getFullYear();
        const month = current.getMonth();
        const first = new Date(year, month, 1);
        const start = new Date(first);
        const dayOffset = first.getDay() === 0 ? 6 : first.getDay() - 1;
        start.setDate(first.getDate() - dayOffset);

        return Array.from({ length: 42 }, (_, index) => {
            const date = new Date(start);
            date.setDate(start.getDate() + index);
            return {
                key: toDateKey(date),
                label: date.getDate(),
                outside: date.getMonth() !== month,
            };
        });
    }, [selectedDate]);

    const isSlotPassed = (timeStr, dateKey) => {
        if (!dateKey) return false;
        const now = new Date();
        const targetDate = new Date(dateKey);

        const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const targetOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

        if (targetOnly > todayOnly) return false;
        if (targetOnly < todayOnly) return true;

        const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (!match) return false;

        let [, hours, minutes, modifier] = match;
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (modifier) {
            if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
            if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
        }

        const slotTime = new Date();
        slotTime.setHours(hours, minutes, 0, 0);
        return slotTime < now;
    };

    /* ---------------- Subject / Notes ---------------- */
    const [subjectId, setSubjectId] = useState("");
    const [notes, setNotes] = useState("");

    /* ---------------- Validation + Submit ---------------- */
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const validate = () => {
        const newErrors = {};
        if (!selectedCustomer) newErrors.customer = t("selectCustomer");
        if (!selectedDate) newErrors.date = t("selectAppointmentDate");
        if (!selectedTime) newErrors.time = t("selectAppointmentTimeSlot");
        if (!subjectId) newErrors.subject = t("selectAppointmentSubject");

        const trimmedNotes = notes.trim();
        if (trimmedNotes.length < 10) {
            newErrors.notes = t("writeNotesReason");
        } else if (trimmedNotes.length > 250) {
            newErrors.notes = t("reasonMaxLength");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            customer_id: selectedCustomer.id,
            appointment_date: selectedDate,
            appointment_time: selectedTime,
            appointment_subject_id: Number(subjectId),
            notes: notes.trim(),
        };

        setSubmitting(true);
        try {
            const res = await dispatch(createAppointmentAdmin(payload)).unwrap();
            if (res?.status) {
                Toaster.success(res?.message || t("appointmentCreatedSuccessfully"));
                navigate("/dashboard/appointments");
            } else {
                Toaster.error(res?.message || "Failed to create appointment.");
            }
        } catch (err) {
            Toaster.error(err?.message || "Failed to create appointment.");
        } finally {
            setSubmitting(false);
        }
    };

    const loadingMoreRef = useRef(false);
    const handleCustomerScroll = (e) => {
        if (loadingMoreRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (
            scrollTop + clientHeight >= scrollHeight - 20 &&
            !customersLoading &&
            customers.current_page < customers.last_page
        ) {
            loadingMoreRef.current = true;

            const nextPage = customers.current_page + 1;

            setCustomerPage(nextPage);
            fetchCustomers(nextPage, customerSearch).finally(() => {
                loadingMoreRef.current = false;
            });
        }
    };

    const inputStyle =
        "block w-full px-4 h-12 border border-gray-300 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-black";

    return (
        <div className="p-3 md:p-6 min-h-screen bg-white">
            <h2 className="text-2xl font-semibold mb-8">{t("createAppointment")}</h2>

            <form onSubmit={handleSubmit}>
                {/* -------- Customer Search -------- */}
                <div className="mb-8" ref={customerBoxRef}>
                    <FormField label={t("customer")} required error={errors.customer}>
                        {selectedCustomer ? (
                            <div className="flex items-center justify-between border border-gray-300 rounded-md bg-gray-50 px-4 h-12">
                                <div className="text-sm">
                                    <span className="font-semibold">{selectedCustomer.name}</span>
                                    {selectedCustomer.company_name && (
                                        <span className="text-gray-500"> — {selectedCustomer.company_name}</span>
                                    )}
                                    <span className="text-gray-500"> ({selectedCustomer.email})</span>
                                </div>
                                <button type="button" onClick={handleClearCustomer}>
                                    <XCircleIcon className="h-5 w-5 text-red-500" />
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="relative">
                                    {!customerSearch && (
                                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    )}
                                    <input
                                        type="text"
                                        placeholder={t("searchCustomerPlaceholder")}
                                        className={`${inputStyle} ${!customerSearch ? 'pl-10' : 'pl-4'}`}
                                        value={customerSearch}
                                        onFocus={handleCustomerFocus}
                                        onChange={(e) => setCustomerSearch(e.target.value)}
                                    />
                                    {customerSearch && (
                                        <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={() => setCustomerSearch('')}>
                                            <XCircleIcon className="h-5 w-5 text-red-500" />
                                        </IconButton>
                                    )}
                                </div>

                                {showCustomerDropdown && (
                                    <div
                                        ref={dropdownRef}
                                        onScroll={handleCustomerScroll}
                                        className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg"
                                    >
                                        {customers?.data?.map((customer) => (
                                            <button
                                                key={customer.id}
                                                type="button"
                                                onClick={() => handleSelectCustomer(customer)}
                                                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 flex items-center gap-3"
                                            >
                                                <img
                                                    src={customer.profile_image || "/img/default-avatar.png"}
                                                    alt={customer.name}
                                                    className="w-8 h-8 rounded-full object-cover bg-gray-200"
                                                />

                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {customer.name}
                                                    </p>

                                                    <p className="text-xs text-gray-500">
                                                        {customer.company_name || "—"} · {customer.email}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}

                                        {customersLoading && (
                                            <div className="flex justify-center py-4">
                                                <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-500" />
                                            </div>
                                        )}

                                        {!customersLoading &&
                                            !customersError &&
                                            customers?.current_page >= customers?.last_page && (
                                                <div className="py-2 text-center text-xs text-gray-400">
                                                    No more customers.
                                                </div>
                                            )}

                                        {!customersLoading && customersError && (
                                            <div className="py-4 text-center text-red-500">
                                                {customersError}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </FormField>
                </div>

                {/* -------- Date + Slots -------- */}
                <div className="grid lg:grid-cols-[minmax(340px,_1fr)_2fr] gap-12 items-start">
                    <section>
                        <h2 className="text-lg font-bold mb-6 text-gray-900">{t("chooseDate")}</h2>

                        <div className="border border-gray-100 rounded-2xl p-6 bg-white shadow-sm">
                            <div className="border-b border-gray-100 pb-5 mb-6">
                                <div className="flex items-center justify-center gap-1 text-base">
                                    <div className="relative inline-flex items-center">
                                        <select
                                            value={currentDate.getMonth()}
                                            onChange={(e) => {
                                                const d = new Date(currentDate);
                                                d.setDate(1);
                                                d.setMonth(Number(e.target.value));
                                                setSelectedDate(toDateKey(d));
                                            }}
                                            className="font-bold text-[#065C36] cursor-pointer border-0 bg-transparent focus:outline-none appearance-none pr-6"
                                        >
                                            {months.map((month, index) => (
                                                <option key={month} value={index}>
                                                    {/* {month} */}
                                                    {t(`months.${month}`)}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDownIcon className="absolute right-0 h-4 w-4 text-gray-500 pointer-events-none" />
                                    </div>

                                    <div className="relative inline-flex items-center pl-2">
                                        <select
                                            value={currentDate.getFullYear()}
                                            onChange={(e) => {
                                                const d = new Date(currentDate);
                                                d.setDate(1);
                                                d.setFullYear(Number(e.target.value));
                                                setSelectedDate(toDateKey(d));
                                            }}
                                            className="font-bold text-gray-900 cursor-pointer border-0 bg-transparent focus:outline-none appearance-none pr-6"
                                        >
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const y = new Date().getFullYear() + i;
                                                return <option key={y} value={y}>{y}</option>;
                                            })}
                                        </select>
                                        <ChevronDownIcon className="absolute right-0 h-4 w-4 text-gray-500 pointer-events-none" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-800 mb-4">
                                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                                    <span key={day} className="py-1">{day}</span>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-y-3 gap-x-1">
                                {monthDays.map((day) => {
                                    const isActive = day.key === selectedDate;
                                    const isPast = day.key < todayKey;
                                    if (day.outside) return <div key={day.key} className="aspect-square" />;

                                    return (
                                        <div key={day.key} className="flex flex-col items-center justify-center relative">
                                            <button
                                                type="button"
                                                disabled={isPast}
                                                onClick={() => setSelectedDate(day.key)}
                                                className={`w-11 h-11 rounded-full text-sm font-medium transition-all flex items-center justify-center
                                                ${isActive ? "bg-[#065C36] text-white font-bold shadow-sm" : "text-gray-700 hover:bg-gray-50"}
                                                ${isPast ? "opacity-30 cursor-not-allowed line-through" : ""}`}
                                            >
                                                {day.label}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {errors.date && <p className="text-red-600 text-xs mt-2 font-medium">{errors.date}</p>}
                    </section>

                    <section>
                        <h2 className="text-lg font-bold mb-6 text-gray-900">{t("pickTime")}</h2>

                        {slotsLoading ? (
                            <div className="flex items-center justify-center py-12 gap-3 text-sm font-medium text-gray-500">
                                <ArrowPathIcon className="w-5 h-5 animate-spin text-[#065C36]" /> Loading slots...
                            </div>
                        ) : availableSlots?.length === 0 ? (
                            <div className="text-center py-12 text-sm text-gray-500 border border-dashed border-gray-200 rounded-2xl bg-gray-50">
                                No slots available for this day.
                            </div>
                        ) : (
                            <div>
                                <div className="grid grid-cols-4 gap-6">
                                    {availableSlots?.map((slot) => {
                                        const isSelected = selectedTime === slot.time;
                                        const isPast = isSlotPassed(slot.time, selectedDate);
                                        const isBooked = !slot.is_available;
                                        const isDisabled = isPast || isBooked;

                                        return (
                                            <button
                                                disabled={isDisabled}
                                                key={slot.time}
                                                type="button"
                                                onClick={() => !isDisabled && setSelectedTime(slot.time)}
                                                className={`h-12 rounded-xl border px-4 flex items-center justify-center text-sm font-semibold transition-all
                                                ${isBooked
                                                        ? "bg-red-100 border-red-400 text-red-600 cursor-not-allowed"
                                                        : isPast
                                                            ? "bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed"
                                                            : isSelected
                                                                ? "bg-[#065C36] border-[#065C36] text-white"
                                                                : "bg-white border-[#065C36] text-[#065C36] hover:bg-emerald-50/40"
                                                    }`}
                                            >
                                                {slot.time}
                                            </button>
                                        );
                                    })}
                                </div>
                                {errors.time && <p className="text-red-600 text-xs mt-3 font-medium">{errors.time}</p>}
                            </div>
                        )}
                    </section>
                </div>

                {/* -------- Subject + Notes -------- */}
                <div className="mt-12">
                    <div className="bg-[#F6F8FB] rounded-2xl p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormField label={t("issueSubject")} required error={errors.subject}>
                                <div className="relative">
                                    <select
                                        className={`${inputStyle} bg-white appearance-none`}
                                        value={subjectId}
                                        onChange={(e) => setSubjectId(e.target.value)}
                                    >
                                        <option value="">{t("selectIssueSubject")}</option>

                                        {Array.isArray(subjectsAdmin) &&
                                            subjectsAdmin.map((item) => (
                                                <option key={item.id} value={item.id}>
                                                    {item.subject}
                                                </option>
                                            ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                                </div>
                            </FormField>

                            <div className="md:col-span-2">
                                <FormField label={t("notesReasonForBooking")} required error={errors.notes}>
                                    <textarea
                                        placeholder={t("bookingReasonPlaceholder")}
                                        className="block w-full px-4 py-3 border border-gray-300 rounded-md text-sm bg-white min-h-[128px] resize-y focus:outline-none focus:ring-1 focus:ring-inset focus:ring-black"
                                        maxLength={2000}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value.replace(/^\s+/, ""))}
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 text-center md:text-left">
                        <Button
                            type="submit"
                            disabled={submitting || loading}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-[80px] rounded-xl normal-case flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {submitting && (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            {submitting ? t("booking") : t("createAppointment")}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}